<template>
    <div>
        <v-container>
            <v-row>
                <v-col cols="12" md="4" xs="12" sm="12">

                    <v-chip class="ma-2 text-right" label
                            color="green">
                        <v-icon left>
                            mdi-alpha-w-box
                        </v-icon> Wordcloud Chart Builder
                    </v-chip>

                    <v-text-field v-model="title"
                                  :rules="titleRules"
                                  :counter="200"
                                  label="Chart Title"
                                  required></v-text-field>

                    <v-select :items="tableColumns"
                              item-text="friendlyName"
                              return-object
                              v-model="selectedColumn"
                              label="Source"></v-select>

                    <v-expansion-panels>
                        <v-expansion-panel v-if="selectedColumn">
                            <v-expansion-panel-header>
                                Chart Filters
                                <span class="ml-1" v-if="chartFilters.length > 0">
                                    ({{chartFilters.length}})
                                </span>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>

                                <p class="caption mb-4">Select a source and apply any given filters to the chart, you can add more than one filter at a time.</p>

                                <v-select :items="filterableTableColumns"
                                          item-text="friendlyName"
                                          return-object
                                          v-model="selectedFilterColumn"
                                          label="Source"></v-select>

                                <v-select v-model="selectedColumnFilters"
                                          :items="sourceItems"
                                          attach
                                          chips
                                          label="Chart Filters"
                                          multiple>
                                    <template v-slot:append>
                                        <v-chip color="primary" @click="onOpenNewFilterOption" v-if="selectedFilterColumn" class="ma-2">
                                            <v-icon>
                                                mdi-plus
                                            </v-icon> Add New Item
                                        </v-chip>
                                    </template>
                                    <template v-slot:prepend-item>
                                        <v-list-item ripple
                                                     @click="toggleSourceItems">
                                            <v-list-item-action>
                                                <v-icon :color="selectedColumnFilters.length > 0 ? 'primary darken-4' : ''">
                                                    {{ sourceIcon }}
                                                </v-icon>
                                            </v-list-item-action>
                                            <v-list-item-content>
                                                <v-list-item-title>
                                                    Select All
                                                </v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-divider class="mt-2"></v-divider>
                                    </template>

                                </v-select>

                                <v-row>
                                    <v-col class="text-right">
                                        <v-btn outlined
                                               rounded
                                               class="mt-4"
                                               @click="onAddColumnFilter"
                                               text>
                                            Add Filter
                                        </v-btn>
                                    </v-col>
                                </v-row>

                                <v-divider v-if="chartFilters.length > 0" class="mt-4 mb-4"></v-divider>

                                <v-card v-for="(filter, index) in chartFilters" :key="filter.name" class="pt-4 mt-4 pa-2">
                                    <v-row>
                                        <v-col cols="12" md="4" lg="4">
                                            <div class="caption">Filter {{index + 1}}</div>
                                            {{filter.friendlyName}}
                                        </v-col>
                                        <v-col cols="12" md="6" lg="6">
                                            <v-chip class="ma-1" v-for="name in filter.columnFilters" :key="name">
                                                {{name}}
                                            </v-chip>
                                        </v-col>
                                        <v-col cols="12" md="2" lg="2">
                                            <v-btn outlined
                                                   rounded
                                                   small
                                                   @click="onRemoveColumnFilter(filter.reportTableColumnId)"
                                                   class="mt-4"
                                                   text>
                                                <v-icon dark>
                                                    mdi-delete
                                                </v-icon>
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-card>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                        <v-expansion-panel>
                            <v-expansion-panel-header>
                                Date Range
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <div class="caption pb-6">The default number of days in the past a report will use to retrieve data</div>
                                <v-slider v-model="selectedDays"
                                          class="mt-6"
                                          max="365"
                                          thumb-label="always"></v-slider>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                        <v-expansion-panel>
                            <v-expansion-panel-header>
                                Styles
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <v-row>
                                    <v-col cols="6" md="6" xs="12" sm="12">

                                        <!-- ROTATION -->
                                        <v-subheader class="mt-1 mb-3 pl-0">Rotation</v-subheader>
                                        <v-slider v-model="rotation"
                                                  :thumb-size="24"
                                                  max="180"
                                                  min="-180"
                                                  thumb-label="always">
                                        </v-slider>
                                    </v-col>
                                    <v-col cols="6" md="6" xs="12" sm="12">
                                        <!-- ROTATION THRESHOLD -->
                                        <v-subheader class="mt-1 mb-3 pl-0">Rotation Threshhold</v-subheader>
                                        <v-slider v-model="rotationThreshold"
                                                  :thumb-size="24"
                                                  max="100"
                                                  min="0"
                                                  thumb-label="always">
                                            <template v-slot:thumb-label>
                                                {{rotationThreshold}}%
                                            </template>
                                        </v-slider>
                                    </v-col>
                                    <v-col cols="6" md="6" xs="12" sm="12">
                                        <!-- MIN FONT PERCENTAGE-->
                                        <v-subheader class="mt-1 mb-3 pl-0">Min Font Percentage</v-subheader>
                                        <v-slider v-model="minFontSize"
                                                  :thumb-size="24"
                                                  max="100"
                                                  min="2"
                                                  thumb-label="always">
                                            <template v-slot:thumb-label>
                                                {{minFontSize}}%
                                            </template>
                                        </v-slider>
                                    </v-col>
                                    <v-col cols="6" md="6" xs="12" sm="12">
                                        <!-- MAX FONT PERCENTAGE-->
                                        <v-subheader class="mt-1 mb-3 pl-0">Max Font Percentage</v-subheader>
                                        <v-slider v-model="maxFontSize"
                                                  :thumb-size="24"
                                                  max="100"
                                                  :min="minFontSize"
                                                  thumb-label="always">
                                            <template v-slot:thumb-label>
                                                {{maxFontSize}}%
                                            </template>
                                        </v-slider>
                                    </v-col>

                                    <v-col cols="6" md="6" xs="12" sm="12">
                                        <!-- ACCURACY-->
                                        <v-subheader class="mt-1 mb-3 pl-0">Accuracy</v-subheader>
                                        <v-slider v-model="accuracy"
                                                  :thumb-size="24"
                                                  max="100"
                                                  min="0"
                                                  thumb-label="always">
                                            <template v-slot:thumb-label>
                                                {{accuracy}}%
                                            </template>
                                        </v-slider>
                                    </v-col>
                                    <v-col cols="6" md="6" xs="12" sm="12">
                                        <!-- STEP-->
                                        <v-subheader class="mt-1 mb-3 pl-0">Step</v-subheader>
                                        <v-slider v-model="step"
                                                  :thumb-size="24"
                                                  max="100"
                                                  :min="0"
                                                  thumb-label="always">
                                            <template v-slot:thumb-label>
                                                {{step}}%
                                            </template>
                                        </v-slider>
                                    </v-col>

                                    <v-col cols="12" md="12" xs="12" sm="12">
                                        <v-btn outlined
                                               rounded
                                               @click="onStylesReset"
                                               text>
                                            Reset
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </v-expansion-panel-content>
                        </v-expansion-panel>

                        <v-expansion-panel>
                            <v-expansion-panel-header>
                                Themes
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <v-item-group mandatory v-model="selectedTheme">
                                    <v-row>
                                        <v-col v-for="(theme, index) in themes"
                                               :key="theme.name"
                                               :value="index"
                                               cols="12"
                                               lg="2"
                                               md="4"
                                               sm="4"
                                               xs="4">
                                            <v-item v-slot="{ active, toggle }">
                                                <v-btn :style="[active ? {'border': '3px solid ' + primaryColor, 'background-image' : 'linear-gradient(to right,' + getThemeColorString(theme) + ')'} : {'border' : 'none',  'background-image' : 'linear-gradient(to right,' + getThemeColorString(theme) + ')'} ] "
                                                       class="d-flex align-center"
                                                       dark
                                                       fab
                                                       @click="toggle">
                                                </v-btn>
                                            </v-item>
                                        </v-col>
                                    </v-row>
                                </v-item-group>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                        <v-expansion-panel>
                            <v-expansion-panel-header>
                                Settings
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <v-switch v-model="ignoreDateFilter"
                                          label="Ignore Date Filter (Report Filters)"></v-switch>
                            </v-expansion-panel-content>
                        </v-expansion-panel>



                    </v-expansion-panels>

                    <v-row class="mt-4" v-if="isAdmin">
                        <v-col cols="12" md="6" xl="6">
                            <p class="caption mb-4">If True the chart will be part of the template meaning it can be used for all reports. or false means only you will be able to view and edit the chart in the "my charts" page.</p>
                        </v-col>
                        <v-col cols="12" md="6" xl="6">
                            <v-switch v-if="isAdmin" v-model="isTemplate"
                                      label="Is Template"></v-switch>
                        </v-col>
                    </v-row>
                </v-col>
                <v-col cols="12" md="8" xs="12" sm="12">
                    <v-skeleton-loader v-show="isBusy"
                                       type="image"></v-skeleton-loader>

                    <v-card style="min-height:400px;" class="text-center" v-show="!isBusy">
                        <div v-if="selectedColumn && wordCloudPreviewItems.length === 0" class="pa-4">No results found try changing the chart filters or date range</div>
                        <word-cloud-chart ref="wordCloudChartComponent"
                                          v-show="selectedColumn"
                                          :data="wordCloudPreviewItems"
                                          :rotation="rotation"
                                          :rotationThreshold="rotationThreshold"
                                          :minFontSize="minFontSize"
                                          :maxFontSize="maxFontSize"
                                          :accuracy="accuracy"
                                          :step="step"
                                          :colors="selectedThemeColors"
                                          :title="title"></word-cloud-chart>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>

        <sweet-modal ref="filterModal" :modal-theme="$vuetify.theme.dark === true ? 'dark' : 'light'" :overlay-theme="$vuetify.theme.dark === true ? 'light' : 'dark'" blocking>

            <v-form v-model="isFilterFormValid" ref="filterForm">
                <v-container>
                    <v-row>
                        <v-col cols="12"
                               md="12">
                            <v-text-field v-model="customFilter"
                                          :rules="filterNameRules"
                                          :counter="200"
                                          label="Filter"
                                          required></v-text-field>
                        </v-col>

                    </v-row>
                    <v-row>
                        <v-col cols="12" align="right">
                            <v-btn depressed
                                   :disabled="!isFilterFormValid"
                                   align="right"
                                   @click="onAddNewFilter"
                                   class="black--text mt-10 font-bold"
                                   color="primary">
                                Add
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-container>
            </v-form>
        </sweet-modal>
    </div>
</template>

<script>
    import WordCloudChart from "./WordCloudChart"
    import reportClient from "../../services/reportService"
    import chartService from "../../services/chartService"
    import config from '../../../public/okta.config.json';
    import ReportTableService from "../../services/reportTableService"
    export default {
        name: 'WordCloudChartBuilder',
        props: {

        },
        components: {
            WordCloudChart
        },
        data: function () {
            return {
                isFilterFormValid: false,
                customFilter: "",
                isAdmin: false,
                isTemplate: true,
                title: "",
                isBusy: false,
                titleRules: [
                    v => !!v || 'Title is required',
                    v => v.length <= 200 || 'Title must be less than 200 characters'],
                filterNameRules: [
                    v => !!v || 'Filter is required',
                    v => v.length <= 200 || 'Filter must be less than 200 characters'],
                accessToken: "",
                selectedColumn: "",
                rotation: 0,
                rotationThreshold: 70,
                accuracy: 4,
                step: 14,
                minFontSize: 5,
                maxFontSize: 20,
                selectedColumnFilters: [],
                sourceItems: [],
                selectedDays: 30,
                tableColumns: [],
                originalTableColumns: [],
                wordCloudPreviewItems: [],
                selectedTheme: 0,
                themes: [
                    {
                        name: "default",
                        colors: ["#fff"]
                    },
                    {
                        name: "custom",
                        colors: ["#B1D4E0", "#2E8BC0", '#0C2D48', '#145DA0']
                    },
                    {
                        name: "custom2",
                        colors: ["#DB1F48", "#004369", '#01949A', '#E5DDC8']
                    },
                    {
                        name: "custom3",
                        colors: ["#333652", "#FAD02C", '#E9EAEC', '#90ADC6']
                    },
                    {
                        name: "custom4",
                        colors: ["#94C973", "#2F5233", '#B1D8B7', '#76B947']
                    },
                    {
                        name: "custom5",
                        colors: ["#F83839", "#513B41", '#7FE5F0', '#C8F4F9']
                    }
                ],
                ignoreDateFilter: false,
                chartFilters: [],
                selectedFilterColumn: null
            }
        },
        created() {
            this.setup();
        },
        watch: {
            deep: true,
            immediate: true,
            selectedFilterColumn() {
                this.getUniqueSourceItems();
            },
            selectedColumn() {
                this.getWordCloudChart();
            },
            selectedDays() {
                this.getWordCloudChart();
            },
            chartFilters() {
                this.getWordCloudChart();
            }
        },
        computed: {
            primaryColor: function () {
                return this.$vuetify.theme.themes[this.$vuetify.theme.dark === true ? "dark" : "light"].primary;
            },
            selectedThemeColors() {
                return this.themes[this.selectedTheme].colors;
            },
            dateRangeText() {
                return this.dates.join(' ~ ')
            },
            sourceAllOptions() {
                return this.selectedColumnFilters.length === this.sourceItems.length
            },
            sourceSomeOptions() {
                return this.selectedColumnFilters.length > 0 && !this.sourceAllOptions
            },
            sourceIcon() {
                if (this.sourceAllOptions) return 'mdi-close-box'
                if (this.sourceSomeOptions) return 'mdi-minus-box'
                return 'mdi-checkbox-blank-outline'
            },
            filterableTableColumns() {
                return this.originalTableColumns.filter(column => column.canFilter);
            }
        },
        methods: {
            async setup() {
                this.accessToken = await this.$auth.getAccessToken();

                var user = await this.$auth.getUser();
                this.isAdmin = user.groups.includes(config.adminGroup);

                await this.getReportTableColumns();

                if (this.$route.params.id && this.$route.params.type === 'WORDCLOUD') {
                    await this.load(this.$route.params.id);
                }
            },
            async load(chartId) {
                if (chartId) {
                    let result = await chartService.getWordCloudChartDetailsById(chartId, this.accessToken);

                    this.isTemplate = result.isTemplate;
                    this.title = result.title;

                    if (result.chartColumns.length > 0) {
                        this.selectedColumn = result.chartColumns[0];
                    }

                    result.chartFilters.forEach((item) => {
                        this.chartFilters.push(item);
                    });

                    this.selectedDays = result.days;

                    let selectedThemeColor = {
                        name: "customSave",
                        colors: result.colors
                    }
                    this.themes.push(selectedThemeColor);
                    this.selectedTheme = this.themes.length - 1;

                    this.rotation = result.rotation;
                    this.rotationThreshold = result.rotationThreshold;
                    this.accuracy = result.accuracy;
                    this.step = result.step;
                    this.minFontSize = result.minFontSize;
                    this.maxFontSize = result.maxFontSize;
                    this.ignoreDateFilter = result.ignoreDateFilter;
                }
            },
            async getUniqueSourceItems() {
                if (this.selectedFilterColumn && !this.chartFilters.some(filter => filter.columnName === this.selectedFilterColumn.columnName)) {
                    this.selectedColumnFilters = [];
                    this.sourceItems = [];
                    var result = await reportClient.getUniqueSourceItems(
                        this.selectedFilterColumn.id,
                        this.accessToken);
                    for (var i = 0; i < result.length; i++) {
                        this.selectedColumnFilters.push(result[i]);
                    }
                    this.sourceItems = result;
                }
            },
            async getWordCloudChart() {
                if (this.selectedColumn && !this.isBusy) {
                    let chartRequest = {
                        chartColumns: [this.selectedColumn],
                        chartFilters: this.chartFilters,
                        days: this.selectedDays
                    }

                    this.isBusy = true;
                    const results = await reportClient.getWordCloudChart(
                        chartRequest,
                        this.accessToken);

                    this.wordCloudPreviewItems = results;

                    this.isBusy = false;
                }
            },
            toggleSourceItems() {
                this.$nextTick(() => {
                    if (this.sourceAllOptions) {
                        this.selectedColumnFilters = []
                    } else {
                        this.selectedColumnFilters = this.sourceItems.slice()
                    }
                })
            },
            async onStylesReset() {
                this.rotation = 0;
                this.rotationThreshold = 70;
                this.accuracy = 4;
                this.step = 14;
                this.minFontSize = 3;
                this.maxFontSize = 30;
            },
            async getThemeBackground(colors) {
                if (colors) {
                    const backgroundColor = 'red';
                    return backgroundColor;
                }
                return "";

            },
            dispose() {
                this.$refs.wordCloudChartComponent.dispose();
            },
            getChartDetails() {
                return {
                    chartColumns: [this.selectedColumn],
                    chartFilters: this.chartFilters,
                    days: this.selectedDays,
                    rotation: this.rotation,
                    rotationThreshold: this.rotationThreshold,
                    accuracy: this.accuracy,
                    step: this.step,
                    minFontSize: this.minFontSize,
                    maxFontSize: this.maxFontSize,
                    colors: this.selectedThemeColors,
                    title: this.title,
                    isTemplate: this.isTemplate && this.isAdmin,
                    ignoreDateFilter: this.ignoreDateFilter,
                };
            },
            getThemeColorString(theme) {
                if (theme.colors.length === 0) return "#000,#000";
                return theme.colors.length > 1 ? theme.colors.join(',') : `${theme.colors[0]},${theme.colors[0]}`;
            },
            async getReportTableColumns() {
                let reportTables = await ReportTableService.getReportTables(this.accessToken);

                if (reportTables) {
                    let reportTableId = "";
                    reportTables.forEach((reportTable) => {
                        if (reportTable.name === "vw_PostSev_All") {
                            reportTableId = reportTable.id;
                        }
                    });

                    let result = await ReportTableService.searchReportTableColumns(
                        reportTableId,
                        "",
                        1,
                        100,
                        "ColumnName",
                        "ASCENDING",
                        this.accessToken);

                    if (result && result.data) {
                        this.originalTableColumns = result.data.map(obj => ({ ...obj, reportTableColumnId: obj.id }));
                        this.tableColumns = result.data.filter(data => data.columnType === 0).map(obj => ({ ...obj, reportTableColumnId: obj.id }));
                    }
                }
            },
            async onAddColumnFilter() {

                var chartFilter = {
                    reportTableColumnId: this.selectedFilterColumn.id,
                    columnName: this.selectedFilterColumn.columnName,
                    columnFilters: this.selectedColumnFilters,
                    friendlyName: this.selectedFilterColumn.friendlyName
                }

                this.chartFilters.push(chartFilter);

                this.selectedFilterColumn = null;
                this.selectedColumnFilters = [];

                await this.getWordCloudChart();
            },
            onRemoveColumnFilter(id) {
                this.chartFilters = this.chartFilters.filter((item) => item.reportTableColumnId !== id);
            },
            onOpenNewFilterOption() {
                this.$refs.filterModal.open();
                this.customFilter = "";
                this.$refs.filterForm.resetValidation();
            },
            onAddNewFilter() {
                this.sourceItems.push(this.customFilter);
                this.selectedColumnFilters.push(this.customFilter);
                this.customFilter = "";
                this.$refs.filterModal.close();
            }
        },
    }
</script>
